import React, { useCallback, useEffect, useRef, useState } from "react";
import { WebsiteBuilderBanner } from "./shell/banner/WebsiteBuilderBanner";
import { WebsiteBuilderToolbar } from "./shell/toolbar/WebsiteBuilderToolbar";
import { WebsiteBuilderPanel } from "./shell/panel/WebsiteBuilderPanel";
import { WebsiteBuilderDesignSpace } from "./designSpace/WebsiteBuilderDesignSpace";
import { TWebsiteBuilderTabsetActiveTabMap, WebsiteBuilderEditorContext } from "./websiteBuilderEditorContext";
import { TWebsiteBuilderActivePageDomNodeRefMap } from "./hooks/activePageDomNodeRefMap/useActivePageDomNodeRefMapRegistration";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useWebsiteBuilderDndManager } from "./hooks/dndManager/useWebsiteBuilderDndManager";
import { useWebsiteBuilderToolbarState } from "./hooks/toolbarState/useWebsiteBuilderToolbarState";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import {
  websiteBuilderRedo,
  websiteBuilderSelectNode,
  websiteBuilderUndo,
} from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { useWebsiteBuilderElementOperations } from "./hooks/nodeOperations/elementOperations/useWebsiteBuilderElementOperations";
import { isWebsiteBuilderElementParentNode } from "./nodes/websiteBuilderNode";
import { useWebsiteBuilderClipboard } from "./hooks/clipboard/useWebsiteBuilderClipboard";
import { useWebsiteBuilderSectionOperations } from "./hooks/nodeOperations/sectionOperations/useWebsiteBuilderSectionOperations";
import { WebsiteBuilderPreview } from "./preview/WebsiteBuilderPreview";
import { WebsiteBuilderLoadingScreen } from "./shell/loadingScreen/WebsiteBuilderLoadingScreen";
import { useLoadWebsiteBuilderEditor } from "./hooks/loadEditor/useLoadWebsiteBuilderEditor";
import { useParams } from "react-router";
import { WEBSITE_BUILDER_ROOT_ID } from "./nodes/root/websiteBuilderRoot";
import { WebsiteBuilderScrollToNodeEvent } from "redux/reducers/websiteBuilder/websiteBuilderMiddleware";
import { TWebsiteBuilderOverlayLinesRef } from "./designSpace/overlay/WebsiteBuilderOverlay";
import { useWebsiteBuilderContainerOperations } from "./hooks/nodeOperations/containerOperations/useWebsiteBuilderContainerOperations";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import { useTranslation } from "react-i18next";
import "./websiteBuilderEditor.scss";
import { isWebsiteBuilderTextEditableTarget } from "./utility/websiteBuilderTextEditableTarget";

export interface IWebsiteBuilderDesignSpaceBoundaries {
  leftX: number;
  rightX: number;
  width: number;
}

export type TWebsiteBuilderScrollContainerRef = React.MutableRefObject<HTMLDivElement>;
export type TWebsiteBuilderDesignSpaceRef = React.MutableRefObject<HTMLDivElement>;

interface IWebsiteBuilderEditorParams {
  pastWebsiteVersionId?: string;
}

export const WEBSITE_BUILDER_PORTAL = "website-builder-portal";
export const WEBSITE_BUILDER_DRAG_LAYER = "website-builder-drag-layer";

export function WebsiteBuilderEditor() {
  const { pastWebsiteVersionId: pastWebsiteVersionIdParam } = useParams<IWebsiteBuilderEditorParams>();

  const { t } = useTranslation();

  const dndManager = useWebsiteBuilderDndManager();
  
  const { website, editorLoadingStatus } = useLoadWebsiteBuilderEditor({ pastWebsiteVersionIdParam });

  const { toolbarState, handleSetToolbarMenuType } = useWebsiteBuilderToolbarState();
  
  const dispatch = useAppDispatch();
  
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);
  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const activePageNodeMap = useAppSelector(store => store.websiteBuilderStore.editor.activePageNodeMap);
  const history = useAppSelector(store => store.websiteBuilderStore.editor.history);
  const historyIndex = useAppSelector(store => store.websiteBuilderStore.editor.historyIndex);
  const selectedNode = useAppSelector(store => store.websiteBuilderStore.editor.selectedNode);
  const mode = useAppSelector(store => store.websiteBuilderStore.editor.mode);
  const fatalError = useAppSelector(store => store.websiteBuilderStore.editor.fatalError);

  const clipboard = useWebsiteBuilderClipboard();
  
  const elementOperations = useWebsiteBuilderElementOperations(clipboard);
  const containerOperations = useWebsiteBuilderContainerOperations(clipboard);
  const sectionOperations = useWebsiteBuilderSectionOperations(clipboard);
  
  const activePageDomNodeRefMap: TWebsiteBuilderActivePageDomNodeRefMap = useRef(new Map());

  const [tabsetActiveTabMap, setTabsetActiveTabMap] = useState<TWebsiteBuilderTabsetActiveTabMap>(new Map());

  const updateTabsetActiveTab = useCallback((tabsetId: string, activeTabId: string) => {
    setTabsetActiveTabMap(prev => {
      if (prev.has(tabsetId) && prev.get(tabsetId) === activeTabId) {
        return prev;
      }

      const next = new Map(prev);

      next.set(tabsetId, activeTabId);

      return next;
    });
  }, []);

  const removeTabsetActiveTab = useCallback((tabsetId: string) => {
    setTabsetActiveTabMap(prev => {
      if (!prev.has(tabsetId)) {
        return prev;
      }

      const next = new Map(prev);

      next.delete(tabsetId);

      return next;
    });
  }, []);

  const scrollContainerRef: TWebsiteBuilderScrollContainerRef = useRef(null);
  const designSpaceRef: TWebsiteBuilderDesignSpaceRef = useRef(null);

  const overlayLinesRef: TWebsiteBuilderOverlayLinesRef = useRef({
    horizontal: null,
    vertical: null,
  });

  const [designSpaceBoundaries, setDesignSpaceBoundaries] = useState<IWebsiteBuilderDesignSpaceBoundaries>({
    leftX: 0,
    rightX: 0,
    width: 0,
  });

  const websiteId = website?.id;

  const showLoadingScreen = editorLoadingStatus === "standby" || editorLoadingStatus === "loading";

  const handleKeydown = useCallback(
    (e: KeyboardEvent) => {
      const modifierKeyPressed = e.ctrlKey || e.metaKey;

      if (modifierKeyPressed && e.key.toLowerCase() === "z" && !isWebsiteBuilderTextEditableTarget(e.target)) {
        e.preventDefault();
      }

      if (isWebsiteBuilderTextEditableTarget(e.target) || editorLoadingStatus !== "succeeded" || mode !== "design" || dndManager.isDragging || (selectedNode != null && selectedNode.editingMode.type !== "none")) {
        return;
      }

      if (modifierKeyPressed && e.key.toLowerCase() === "z") {
        if (e.shiftKey) {
          dispatch(websiteBuilderRedo(history, historyIndex, activePageId, deviceView));
        } else {
          dispatch(websiteBuilderUndo(history, historyIndex, activePageId, deviceView));
        }
      } else if (selectedNode != null) {
        if (e.key === "Escape") {
          dispatch(websiteBuilderSelectNode({ selectedNode: null }));
          
          return;
        }

        const node = activePageNodeMap.get(selectedNode.id);

        if (selectedNode.type === "element" && node?.type === "element") {
          const sectionNode = activePageNodeMap.get(selectedNode.sectionId);
          let parentNode = sectionNode;

          if (selectedNode.containerId != null) {
            parentNode = activePageNodeMap.get(selectedNode.containerId);
          }

          if (selectedNode.gridCellId != null) {
            parentNode = activePageNodeMap.get(selectedNode.gridCellId);
          }

          if (selectedNode.tabId != null) {
            parentNode = activePageNodeMap.get(selectedNode.tabId);
          }

          if (sectionNode?.type !== "section" || !isWebsiteBuilderElementParentNode(parentNode)) {
            return;
          }

          if (modifierKeyPressed && e.key.toLowerCase() === "c") {
            elementOperations.copyElement(node);
          } else if (modifierKeyPressed && e.key.toLowerCase() === "v") {
            elementOperations.pasteAtElement(node, parentNode, sectionNode, activePageId, deviceView);
          } else if (modifierKeyPressed && e.key.toLowerCase() === "d") {
            e.preventDefault();
            elementOperations.duplicateElement(node, parentNode, sectionNode, activePageId, deviceView);
          } else if (modifierKeyPressed && e.key.toLowerCase() === "x") {
            elementOperations.cutElement(node, parentNode, activePageId, deviceView);
          } else if (modifierKeyPressed && e.key === "ArrowUp") {
            e.preventDefault();
            elementOperations.bringToFrontElement(node, parentNode, activePageId, deviceView);
          } else if (modifierKeyPressed && e.key === "ArrowDown") {
            e.preventDefault();
            elementOperations.bringToBackElement(node, parentNode, activePageId, deviceView);
          } else if (e.key === "Backspace") {
            elementOperations.deleteElement(node, parentNode, activePageId, deviceView);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            elementOperations.nudgeElement("up", e.shiftKey, node, parentNode, sectionNode, activePageId, deviceView);
          } else if (e.key === "ArrowRight") {
            e.preventDefault();
            elementOperations.nudgeElement("right", e.shiftKey, node, parentNode, sectionNode, activePageId, deviceView);
          } else if (e.key === "ArrowDown") {
            e.preventDefault();
            elementOperations.nudgeElement("down", e.shiftKey, node, parentNode, sectionNode, activePageId, deviceView);
          } else if (e.key === "ArrowLeft") {
            e.preventDefault();
            elementOperations.nudgeElement("left", e.shiftKey, node, parentNode, sectionNode, activePageId, deviceView);
          }
        } else if (selectedNode.type === "container" && node?.type === "container") {
          const parentNode = activePageNodeMap.get(selectedNode.sectionId);
          
          if (parentNode?.type !== "section") {
            return;
          }

          if (modifierKeyPressed && e.key.toLowerCase() === "c") {
            containerOperations.copyContainer(node);
          } else if (modifierKeyPressed && e.key.toLowerCase() === "v") {
            containerOperations.pasteAtContainer(
              node,
              parentNode,
              activePageId,
              deviceView,
              tabsetActiveTabMap,
              activePageDomNodeRefMap,
              scrollContainerRef,
            );
          } else if (modifierKeyPressed && e.key.toLowerCase() === "d") {
            e.preventDefault();
            containerOperations.duplicateContainer(node, parentNode, activePageId, deviceView);
          } else if (modifierKeyPressed && e.key.toLowerCase() === "x") {
            containerOperations.cutContainer(node, parentNode, activePageId, deviceView);
          } else if (modifierKeyPressed && e.key === "ArrowUp") {
            e.preventDefault();
            containerOperations.bringToFrontContainer(node, parentNode, activePageId, deviceView);
          } else if (modifierKeyPressed && e.key === "ArrowDown") {
            e.preventDefault();
            containerOperations.bringToBackContainer(node, parentNode, activePageId, deviceView);
          } else if (e.key === "Backspace") {
            containerOperations.deleteContainer(node, parentNode, activePageId, deviceView);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            containerOperations.nudgeContainer("up", e.shiftKey, node, parentNode, activePageId, deviceView);
          } else if (e.key === "ArrowRight") {
            e.preventDefault();
            containerOperations.nudgeContainer("right", e.shiftKey, node, parentNode, activePageId, deviceView);
          } else if (e.key === "ArrowDown") {
            e.preventDefault();
            containerOperations.nudgeContainer("down", e.shiftKey, node, parentNode, activePageId, deviceView);
          } else if (e.key === "ArrowLeft") {
            e.preventDefault();
            containerOperations.nudgeContainer("left", e.shiftKey, node, parentNode, activePageId, deviceView);
          }
        } else if (selectedNode.type === "section" && node?.type === "section") {
          const root = activePageNodeMap.get(WEBSITE_BUILDER_ROOT_ID);

          if (root?.type !== "root") {
            return;
          }

          if (modifierKeyPressed && e.key.toLowerCase() === "c") {
            sectionOperations.copySection(node);
          } else if (modifierKeyPressed && e.key.toLowerCase() === "v") {
            sectionOperations.pasteAtSection(
              node,
              root,
              activePageId,
              deviceView,
              activePageDomNodeRefMap,
              scrollContainerRef,
            );
          } else if (modifierKeyPressed && e.key.toLowerCase() === "d") {
            e.preventDefault();
            sectionOperations.duplicateSection(node, root, activePageId, deviceView, activePageDomNodeRefMap);
          } else if (modifierKeyPressed && e.key.toLowerCase() === "x") {
            sectionOperations.cutSection(node, root, activePageId, deviceView);
          } else if (e.key === "Backspace") {
            sectionOperations.deleteSection(node, root, activePageId, deviceView);
          }
        }
      }
    },
    [
      editorLoadingStatus,
      mode,
      toolbarState.menuType,
      dndManager.isDragging,
      selectedNode,
      history,
      historyIndex,
      activePageId,
      activePageNodeMap,
      tabsetActiveTabMap,
      elementOperations,
      containerOperations,
      sectionOperations,
      deviceView,
    ],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown);

    return () => document.removeEventListener("keydown", handleKeydown);
  }, [handleKeydown]);

  const handleScrollToNodeEvent = useCallback((e: WebsiteBuilderScrollToNodeEvent) => {
    const scrollToNode = activePageDomNodeRefMap.current.get(e.detail.id);

    if (scrollToNode != null) {
      scrollToNode.scrollIntoView({ behavior: "instant", block: "center" });
    }
  }, []);

  useEffect(() => {
    window.addEventListener(WebsiteBuilderScrollToNodeEvent.TYPE, handleScrollToNodeEvent);

    return () => window.removeEventListener(WebsiteBuilderScrollToNodeEvent.TYPE, handleScrollToNodeEvent);
  }, [handleScrollToNodeEvent]);

  function renderModeContent() {
    switch (mode) {
      case "design": {
        return (
          <>
            <WebsiteBuilderDesignSpace setDesignSpaceBoundaries={setDesignSpaceBoundaries} />
            <WebsiteBuilderToolbar type="active" toolbarState={toolbarState} handleSetToolbarMenuType={handleSetToolbarMenuType} />
            <WebsiteBuilderPanel />
          </>
        );
      }
      case "preview": return <WebsiteBuilderPreview />;
      default: return null;
    }
  }

  function renderEditor() {
    if (editorLoadingStatus === "succeeded" && fatalError.type === "none") {
      return (
        <WebsiteBuilderEditorContext.Provider
          value={{
            activePageDomNodeRefMap,
            tabsetActiveTabMap,
            updateTabsetActiveTab,
            removeTabsetActiveTab,
            elementOperations,
            containerOperations,
            sectionOperations,
            clipboard,
            designSpaceBoundaries,
            designSpaceRef,
            scrollContainerRef,
            overlayLinesRef,
            websiteId,
            handleSetToolbarMenuType,
          }}
        >
          <WebsiteBuilderBanner type="active" />
          {renderModeContent()}
        </WebsiteBuilderEditorContext.Provider>
      );
    } else {
      return null;
    }
  }

  function renderFatalErrorSheet() {
    if (fatalError.type === "none") {
      return null;
    } else {
      return (
        <Portal isMounted={true} parentId={WEBSITE_BUILDER_PORTAL}>
          <Sheet
            open={true}
            title={t("Migration Error")}
            size="small"
            hideCancelButton
          >
            <div className="website-builder-editor-fatal-error-sheet-message">{fatalError.message}</div>
            <div>{t("Refreshing the editor may fix the issue. If the issue persists contact customer support.")}</div>
          </Sheet>
        </Portal>
      );
    }
  }

  return (
    <>
      {showLoadingScreen ? (
        <WebsiteBuilderLoadingScreen>
          <WebsiteBuilderBanner type="skeleton" />
          <WebsiteBuilderToolbar type="skeleton" />
        </WebsiteBuilderLoadingScreen>
      ): (
        renderEditor()
      )}

      {renderFatalErrorSheet()}

      <div id={WEBSITE_BUILDER_PORTAL} />
      <div id={WEBSITE_BUILDER_DRAG_LAYER} />
    </>
  );
}

export function WebsiteBuilderEditorContainer() {
  return (
    <DndProvider backend={HTML5Backend}>
      <WebsiteBuilderEditor />
    </DndProvider>
  );
}
